import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, FileText, FileDown, Filter, AlertTriangle, Search, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { customerReceivedNcService, clientService, productService } from '../services/api';

const STATUS_COLORS = {
  'Ricevuta': 'bg-sky-500 text-white',
  'In valutazione': 'bg-amber-500 text-white',
  'In risposta': 'bg-blue-500 text-white',
  'In attesa di riscontro': 'bg-violet-500 text-white',
  'Accettata': 'bg-emerald-600 text-white',
  'Parzialmente accettata': 'bg-emerald-400 text-white',
  'Rifiutata': 'bg-red-600 text-white',
  'Chiusa': 'bg-slate-500 text-white',
};

const OUTCOME_COLORS = {
  'Accettata': 'bg-emerald-100 text-emerald-800',
  'Parzialmente accettata': 'bg-amber-100 text-amber-800',
  'Rifiutata': 'bg-red-100 text-red-800',
  'Da valutare': 'bg-slate-100 text-slate-700',
};

const emptyFilters = {
  customer_id: '',
  product_id: '',
  status: '',
  technical_outcome: '',
  from_date: '',
  to_date: '',
  search: '',
  open_only: false,
  overdue_only: false,
};

const emptyForm = () => ({
  customer_id: '',
  product_id: '',
  customer_external_nc_number: '',
  received_date: new Date().toISOString().slice(0, 10),
  customer_order_reference: '',
  delivery_reference: '',
  quantity_involved: '',
  customer_defect_description: '',
  customer_request: '',
  response_deadline: '',
  status: 'Ricevuta',
  notes: '',
});

export default function CustomerReceivedNcsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ncs, setNcs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [enums, setEnums] = useState({ statuses: [], technical_outcomes: [] });
  const [filters, setFilters] = useState({
    ...emptyFilters,
    open_only: searchParams.get('open_only') === 'true',
    overdue_only: searchParams.get('overdue_only') === 'true',
  });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== false && v !== null && v !== undefined) params[k] = v;
      });
      const res = await customerReceivedNcService.getAll(params);
      setNcs(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento NC cliente');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, pRes, eRes] = await Promise.all([
          clientService.getAll(),
          productService.getAll(),
          customerReceivedNcService.enums(),
        ]);
        setCustomers(cRes.data || []);
        setProducts(pRes.data || []);
        setEnums(eRes.data || { statuses: [], technical_outcomes: [] });
      } catch { /* silenzio: la pagina lavora anche senza le liste */ }
    })();
  }, []);

  const openReport = (id) => window.open(customerReceivedNcService.reportPdfUrl(id), '_blank');
  const openDossier = (id) => window.open(customerReceivedNcService.dossierPdfUrl(id), '_blank');

  const handleCreate = async () => {
    if (!form.customer_id || !form.customer_defect_description || !form.received_date) {
      toast.error('Compila i campi obbligatori (cliente, difetto, data ricezione)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity_involved: form.quantity_involved ? Number(form.quantity_involved) : null,
        product_id: form.product_id || null,
        customer_order_reference: form.customer_order_reference || null,
        delivery_reference: form.delivery_reference || null,
        customer_request: form.customer_request || null,
        response_deadline: form.response_deadline || null,
        customer_external_nc_number: form.customer_external_nc_number || null,
        notes: form.notes || null,
      };
      const res = await customerReceivedNcService.create(payload);
      toast.success(`Creata ${res.data.nc_code}`);
      setCreateOpen(false);
      setForm(emptyForm());
      navigate(`/customer-received-ncs/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione NC');
    } finally {
      setSaving(false);
    }
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="customer-received-ncs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            Non Conformità Cliente Ricevute
          </h1>
          <p className="text-sm text-slate-500 mt-1">Reclami cliente: registrazione, analisi, risposta, allegati e stampa fascicolo</p>
        </div>
        <Button onClick={() => { setForm(emptyForm()); setCreateOpen(true); }} data-testid="new-crnc-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuova NC Cliente
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2 space-y-1">
              <Label className="text-xs">Ricerca (codice, N° cliente, difetto)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-8"
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  placeholder="Cerca..."
                  data-testid="filter-search"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cliente</Label>
              <Select value={filters.customer_id || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, customer_id: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-customer"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutti</SelectItem>{customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Prodotto</Label>
              <Select value={filters.product_id || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, product_id: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-product"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutti</SelectItem>{products.map(p => (<SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Stato</Label>
              <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutti</SelectItem>{(enums.statuses || []).map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Esito tecnico</Label>
              <Select value={filters.technical_outcome || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, technical_outcome: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-outcome"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutti</SelectItem>{(enums.technical_outcomes || []).map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Dal</Label>
              <Input type="date" value={filters.from_date} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} data-testid="filter-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Al</Label>
              <Input type="date" value={filters.to_date} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} data-testid="filter-to" />
            </div>
            <div className="flex items-end gap-4 md:col-span-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={filters.open_only} onCheckedChange={v => setFilters(f => ({ ...f, open_only: !!v }))} data-testid="filter-open-only" />
                Solo aperte
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={filters.overdue_only} onCheckedChange={v => setFilters(f => ({ ...f, overdue_only: !!v }))} data-testid="filter-overdue" />
                Solo scadute
              </label>
              <Button variant="ghost" size="sm" onClick={() => setFilters(emptyFilters)} data-testid="filter-reset">
                <Filter className="w-4 h-4 mr-1" /> Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-center py-10">
              <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : ncs.length === 0 ? (
            <p className="text-center text-slate-500 py-10">Nessuna NC cliente trovata</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Codice</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">N° Cliente</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Data</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Cliente</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Prodotto</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Lotto</th>
                    <th className="px-3 py-2 text-center font-medium text-slate-600">Q.tà</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Stato</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Esito Tecnico</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Scadenza</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {ncs.map(nc => {
                    const overdue = nc.response_deadline && nc.response_deadline < todayIso && nc.status !== 'Chiusa';
                    return (
                      <tr key={nc.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`crnc-row-${nc.id}`}>
                        <td className="px-3 py-2 font-mono font-semibold">
                          <Link to={`/customer-received-ncs/${nc.id}`} className="text-sky-700 hover:underline">{nc.nc_code}</Link>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{nc.customer_external_nc_number || '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{nc.received_date}</td>
                        <td className="px-3 py-2">{nc.customer_name || '—'}</td>
                        <td className="px-3 py-2 text-xs">{nc.product_code || '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{nc.lot_code || '—'}</td>
                        <td className="px-3 py-2 text-center">{nc.quantity_involved ?? '—'}</td>
                        <td className="px-3 py-2">
                          <Badge className={`${STATUS_COLORS[nc.status] || 'bg-slate-400 text-white'} text-xs px-2 py-0.5`}>{nc.status}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          {nc.technical_outcome
                            ? <Badge className={`${OUTCOME_COLORS[nc.technical_outcome] || ''} text-xs px-2 py-0.5`}>{nc.technical_outcome}</Badge>
                            : <span className="text-slate-400 text-xs">—</span>}
                        </td>
                        <td className={`px-3 py-2 font-mono text-xs ${overdue ? 'text-red-600 font-bold' : ''}`}>
                          {nc.response_deadline || '—'}
                          {overdue && <span className="ml-1 text-red-500">⚠</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" title="Apri" onClick={() => navigate(`/customer-received-ncs/${nc.id}`)} data-testid={`open-crnc-${nc.id}`}>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" title="Report PDF" onClick={() => openReport(nc.id)} data-testid={`report-crnc-${nc.id}`}>
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" title="Fascicolo PDF completo" onClick={() => openDossier(nc.id)} data-testid={`dossier-crnc-${nc.id}`}>
                              <FileDown className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog nuova NC */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nuova NC Cliente Ricevuta</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label>Cliente *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm(f => ({ ...f, customer_id: v }))}>
                <SelectTrigger data-testid="crnc-form-customer"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Prodotto</Label>
              <Select value={form.product_id || 'none'} onValueChange={(v) => setForm(f => ({ ...f, product_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="crnc-form-product"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Nessuno —</SelectItem>{products.map(p => (<SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Data ricezione *</Label>
              <Input type="date" value={form.received_date} onChange={e => setForm(f => ({ ...f, received_date: e.target.value }))} data-testid="crnc-form-date" />
            </div>
            <div className="space-y-1">
              <Label>N° NC Cliente (esterno)</Label>
              <Input value={form.customer_external_nc_number} onChange={e => setForm(f => ({ ...f, customer_external_nc_number: e.target.value }))} placeholder="es. C-2026/42" />
            </div>
            <div className="space-y-1">
              <Label>Rif. Ordine Cliente</Label>
              <Input value={form.customer_order_reference} onChange={e => setForm(f => ({ ...f, customer_order_reference: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Rif. DDT / Consegna</Label>
              <Input value={form.delivery_reference} onChange={e => setForm(f => ({ ...f, delivery_reference: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Quantità coinvolta</Label>
              <Input type="number" min="0" step="1" value={form.quantity_involved} onChange={e => setForm(f => ({ ...f, quantity_involved: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Scadenza risposta</Label>
              <Input type="date" value={form.response_deadline} onChange={e => setForm(f => ({ ...f, response_deadline: e.target.value }))} />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Descrizione difetto (cliente) *</Label>
              <Textarea rows={3} value={form.customer_defect_description} onChange={e => setForm(f => ({ ...f, customer_defect_description: e.target.value }))} data-testid="crnc-form-defect" />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Richiesta del cliente</Label>
              <Textarea rows={2} value={form.customer_request} onChange={e => setForm(f => ({ ...f, customer_request: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Annulla</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="crnc-form-submit">
              {saving ? 'Creazione...' : 'Crea NC'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
